import { useState } from 'react';
import { Routes, Route, useNavigate, useParams } from 'react-router-dom';
import { Play, RotateCw, Square, PauseCircle, Server } from 'lucide-react';
import { Button, Skeleton } from '@vnetwork/ui/primitives';
import { PageHeader, Breadcrumbs, BreadcrumbItem, Container } from '@vnetwork/ui/layout';
import { StatusPill, EmptyState } from '@vnetwork/ui/data';
import { ConfirmDialog, useToast } from '@vnetwork/ui/feedback';
import { useInstanceDetail, useInstanceAction, useInstanceSummary } from '@/api/vmware/instances';
import { vmTone, vmLabel } from '../shared';
import { OverviewTab } from './OverviewTab';
import { InstanceResize } from './InstanceResize';
import { MonitoringTab } from './MonitoringTab';
import { NetworkTrafficTab } from './NetworkTrafficTab';

/**
 * VMware instance detail — tab shell + power action bar.
 *
 * BUG FIX (power state): instance.status is the @mgmt provisioning status
 * ("active"), NOT the vSphere power state. Real power state lives at
 * instance.metadata.power_state ("POWERED_ON" | "POWERED_OFF" | "SUSPENDED").
 * All disabled logic and the header StatusPill now read metadata.power_state,
 * mirroring @console VMwareDetail/index.tsx L84-136.
 */
export function InstanceDetail() {
  const navigate = useNavigate();
  const { serviceId, instanceId } = useParams<{ serviceId: string; instanceId: string }>();
  const [waiting, setWaiting] = useState<string | false>(false);

  const { data: instance, isLoading, isError, refetch } = useInstanceDetail(serviceId, instanceId, waiting);
  const { data: summary } = useInstanceSummary(serviceId, instanceId);
  const instancesPath = `/services/compute/${serviceId}/instances`;

  // Display name = the guest hostname (DNS name, e.g. "sv.esuhai.com"); fall back to
  // the raw VM name / id only when the guest hasn't reported a hostname.
  const hostnameRaw = summary?.summaryItems?.find((x) => x.label === 'summaryView.dnsNameLabel')?.value;
  const hostname = Array.isArray(hostnameRaw) ? hostnameRaw[0] : (typeof hostnameRaw === 'string' ? hostnameRaw : undefined);

  if (isLoading) {
    return (
      <Container size="xl">
        <Skeleton style={{ height: 28, width: 260, marginBottom: 'var(--vn-space-4)' }} />
        <Skeleton style={{ height: 280 }} />
      </Container>
    );
  }

  if (isError) {
    return (
      <Container size="xl">
        <EmptyState
          icon={<Server size={28} />}
          title="Couldn't load instance"
          description="Something went wrong fetching this virtual machine."
          actions={<Button variant="secondary" onClick={() => refetch()}>Retry</Button>}
        />
      </Container>
    );
  }

  if (!instance) {
    return (
      <Container size="xl">
        <EmptyState
          icon={<Server size={28} />}
          title="Instance not found"
          description="It may have been deleted, or you don't have access to it."
          actions={<Button variant="secondary" onClick={() => navigate(instancesPath)}>Back to instances</Button>}
        />
      </Container>
    );
  }

  // Power state from metadata (real vSphere state), NOT instance.status.
  const powerState = instance.metadata?.power_state;

  return (
    <Container size="xl">
      <PageHeader
        breadcrumbs={
          <Breadcrumbs>
            <BreadcrumbItem
              href={instancesPath}
              onClick={(e) => { e.preventDefault(); navigate(instancesPath); }}
            >
              Instances
            </BreadcrumbItem>
            <BreadcrumbItem current>ID: {instanceId}</BreadcrumbItem>
          </Breadcrumbs>
        }
        title={
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--vn-space-3)', minWidth: 0 }}>
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {hostname || instance.name || instanceId}
            </span>
            {/* StatusPill uses metadata.power_state, not instance.status */}
            <StatusPill tone={vmTone(powerState)}>{vmLabel(powerState)}</StatusPill>
          </span>
        }
        actions={
          <PowerActionBar
            serviceId={serviceId!}
            instanceId={instanceId!}
            powerState={powerState}
            instanceName={hostname || instance.name || instanceId || ''}
            onActionStarted={setWaiting}
          />
        }
      />

      <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
        <Routes>
          <Route index element={<OverviewTab serviceId={serviceId!} instance={instance} />} />
          <Route path="resize" element={<><OverviewTab serviceId={serviceId!} instance={instance} /><InstanceResize serviceId={serviceId!} instance={instance} /></>} />
          <Route path="monitoring" element={<MonitoringTab serviceId={serviceId!} instanceId={instanceId!} />} />
          <Route path="network-traffic" element={<NetworkTrafficTab serviceId={serviceId!} instanceId={instanceId!} />} />
          <Route path="*" element={<OverviewTab serviceId={serviceId!} instance={instance} />} />
        </Routes>
      </div>
    </Container>
  );
}

// ---- Power action bar -------------------------------------------------------

function PowerActionBar({
  serviceId,
  instanceId,
  powerState,
  instanceName,
  onActionStarted,
}: {
  serviceId: string;
  instanceId: string;
  /** metadata.power_state: "POWERED_ON" | "POWERED_OFF" | "SUSPENDED" */
  powerState?: string;
  instanceName: string;
  onActionStarted: (action: string | false) => void;
}) {
  const { show } = useToast();
  const [confirmReboot, setConfirmReboot] = useState(false);
  const [confirmShutdown, setConfirmShutdown] = useState(false);
  const [confirmSuspend, setConfirmSuspend] = useState(false);
  const [pendingAction, setPendingAction] = useState<string | null>(null);

  const action = useInstanceAction(serviceId, instanceId, {
    onSuccess: (_d: unknown, { perform }: { perform: string }) => {
      onActionStarted(perform);
      show({ tone: 'success', title: `${actionLabel(perform)} requested` });
    },
    onError: () => {
      setPendingAction(null);
      show({ tone: 'danger', title: 'Action failed' });
    },
    onSettled: () => setPendingAction(null),
  });

  const run = (perform: 'start' | 'reboot' | 'shutdown' | 'standby') => {
    setPendingAction(perform);
    action.mutate({ perform });
  };

  const busy = action.isPending;
  // Normalise: metadata.power_state is already UPPER_SNAKE ("POWERED_ON" etc.).
  const st = String(powerState ?? '').toUpperCase();
  const isOn = st === 'POWERED_ON';
  const isOff = st === 'POWERED_OFF';
  const isSuspended = st === 'SUSPENDED';

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--vn-space-2)', flexWrap: 'wrap' }}>
      {/* START/RESUME — disabled when already on (mirrors @console L104-109) */}
      {(isOff || isSuspended) && (
        <Button
          size="sm"
          startIcon={<Play size={15} />}
          loading={busy && pendingAction === 'start'}
          disabled={busy && pendingAction !== 'start'}
          onClick={() => run('start')}
        >
          {busy && pendingAction === 'start' ? 'Starting…' : isSuspended ? 'Resume' : 'Start'}
        </Button>
      )}

      {/* RESTART — disabled when off or suspended (mirrors @console L83-96) */}
      <Button
        variant="secondary"
        size="sm"
        startIcon={<RotateCw size={15} />}
        loading={busy && pendingAction === 'reboot'}
        disabled={!isOn || (busy && pendingAction !== 'reboot')}
        onClick={() => setConfirmReboot(true)}
      >
        {busy && pendingAction === 'reboot' ? 'Rebooting…' : 'Restart'}
      </Button>

      {/* STOP — disabled when off (mirrors @console L110-122) */}
      <Button
        variant="secondary"
        size="sm"
        startIcon={<Square size={15} />}
        loading={busy && pendingAction === 'shutdown'}
        disabled={isOff || (busy && pendingAction !== 'shutdown')}
        onClick={() => setConfirmShutdown(true)}
      >
        {busy && pendingAction === 'shutdown' ? 'Shutting down…' : 'Shutdown'}
      </Button>

      {/* SUSPEND — disabled when already suspended (verb: standby, mirrors @console L123-136) */}
      <Button
        variant="secondary"
        size="sm"
        startIcon={<PauseCircle size={15} />}
        loading={busy && pendingAction === 'standby'}
        disabled={isSuspended || (busy && pendingAction !== 'standby')}
        onClick={() => setConfirmSuspend(true)}
      >
        {busy && pendingAction === 'standby' ? 'Suspending…' : 'Suspend'}
      </Button>

      <ConfirmDialog
        open={confirmReboot}
        onOpenChange={setConfirmReboot}
        title="Restart instance?"
        description={`To avoid losing progress, please save any unsaved work before "${instanceName}" restarts.`}
        confirmLabel="Restart"
        loading={busy}
        onConfirm={() => { run('reboot'); setConfirmReboot(false); }}
      />
      <ConfirmDialog
        open={confirmShutdown}
        onOpenChange={setConfirmShutdown}
        title="Shutdown instance?"
        description={`To avoid losing progress, please save any unsaved work before "${instanceName}" goes offline.`}
        confirmLabel="Shutdown"
        loading={busy}
        onConfirm={() => { run('shutdown'); setConfirmShutdown(false); }}
      />
      <ConfirmDialog
        open={confirmSuspend}
        onOpenChange={setConfirmSuspend}
        title="Suspend instance?"
        description={`"${instanceName}" will be temporarily paused, capturing its current running state.`}
        confirmLabel="Suspend"
        loading={busy}
        onConfirm={() => { run('standby'); setConfirmSuspend(false); }}
      />
    </div>
  );
}

function actionLabel(perform: string): string {
  const map: Record<string, string> = {
    start: 'Start',
    reboot: 'Restart',
    shutdown: 'Shutdown',
    standby: 'Suspend',
  };
  return map[perform] ?? perform;
}
